import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PaginationControls } from '@/components/PaginationControls';
import { useAdminMarketingApplicationsPage, useMarketingCampaigns, useMarketingMutation } from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, applicationStatusLabel, applicationStatusVariant, bpsToPercent,
  formatPeriod, promotionLabel, type ApplicationStatus, type PromotionType,
} from '@/lib/marketing';

const PAGE_SIZE = 50;

const STATUSES: ApplicationStatus[] = ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'WITHDRAWN'];

export default function AdminMarketingApplications() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('SUBMITTED');
  const [type, setType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [campaignId, setCampaignId] = useState('');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(1), [status, type, debouncedSearch]);

  // FR-97: server-side pages (50 each) and merchant-name search instead of the first 15 rows.
  const { data, isLoading } = useAdminMarketingApplicationsPage({ status, promotion_type: type, search: debouncedSearch, page, per_page: PAGE_SIZE });
  const applications = data?.items ?? [];
  const meta = data?.meta;
  const { data: campaigns = [] } = useMarketingCampaigns();

  const review = useMarketingMutation('start_review');
  const approve = useMarketingMutation('approve_application');
  const reject = useMarketingMutation('reject_application');
  const requestChanges = useMarketingMutation('request_changes');
  const addComment = useMarketingMutation('add_internal_comment');

  const openDrawer = (a: any) => {
    setSelected(a);
    setComment(a.admin_comment || '');
    setReason('');
    setCampaignId('');
  };

  const matchingCampaigns = campaigns.filter((c: any) =>
    selected && c.promotion_type === selected.promotion_type && ['DRAFT', 'SCHEDULED', 'ACTIVE'].includes(c.status));

  const done = (msg: string) => ({
    onSuccess: () => { toast.success(msg); setSelected(null); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <h1 className="mb-4 text-xl font-semibold text-foreground">{t('mkt_applications.title')}</h1>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input className="min-w-[200px] flex-1" placeholder={t('mkt_applications.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_applications.allStatuses')}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{applicationStatusLabel[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_applications.allTypes')}</SelectItem>
            {PROMOTION_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{promotionLabel[pt]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_applications.colStore')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_applications.colType')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_applications.colPeriod')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_applications.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('mkt_applications.loading')}</td></tr>
            ) : applications.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('mkt_applications.empty')}</td></tr>
            ) : applications.map((a: any) => (
              <tr key={a.id} onClick={() => openDrawer(a)} className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/30">
                <td className="p-3">
                  <p className="text-sm font-medium text-foreground">{a.merchant?.name ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">{a.category?.name ?? a.promo_title ?? '—'}</p>
                </td>
                <td className="p-3 text-sm text-muted-foreground">{promotionLabel[a.promotion_type as PromotionType]}</td>
                <td className="p-3 text-sm text-muted-foreground">{formatPeriod(a.requested_start_at, a.requested_end_at)}</td>
                <td className="p-3"><Badge variant={applicationStatusVariant[a.status as ApplicationStatus]}>{applicationStatusLabel[a.status as ApplicationStatus]}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {meta && (
        <PaginationControls
          page={meta.current_page}
          totalPages={meta.last_page}
          total={meta.total}
          pageSize={meta.per_page}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(meta.last_page, p + 1))}
          hasPrev={meta.current_page > 1}
          hasNext={meta.current_page < meta.last_page}
        />
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.merchant?.name} — {promotionLabel[selected.promotion_type as PromotionType]}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-muted-foreground">{t('mkt_applications.colPeriod')}</p><p>{formatPeriod(selected.requested_start_at, selected.requested_end_at)}</p></div>
                  <div><p className="text-xs text-muted-foreground">{t('mkt_applications.colStatus')}</p><p>{applicationStatusLabel[selected.status as ApplicationStatus]}</p></div>
                  {selected.category?.name && (
                    <div><p className="text-xs text-muted-foreground">{t('mkt_applications.category')}</p><p>{selected.category.name}</p></div>
                  )}
                  {selected.proposed_cashback_bps && (
                    <div><p className="text-xs text-muted-foreground">{t('mkt_applications.proposedCashback')}</p><p>{bpsToPercent(selected.proposed_cashback_bps)}</p></div>
                  )}
                  <div><p className="text-xs text-muted-foreground">{t('mkt_applications.storeStatus')}</p><p>{selected.merchant?.platform_status ?? '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">{t('mkt_applications.trustLevel')}</p><p>{selected.merchant?.trust_level ?? '—'}</p></div>
                </div>
                {selected.promo_title && <div><p className="text-xs text-muted-foreground">{t('mkt_applications.promoTitle')}</p><p>{selected.promo_title}</p></div>}
                {selected.merchant_comment && <div><p className="text-xs text-muted-foreground">{t('mkt_applications.merchantComment')}</p><p>{selected.merchant_comment}</p></div>}

                <div>
                  <Label>{t('mkt_applications.internalComment')}</Label>
                  <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
                  <Button size="sm" variant="outline" className="mt-2"
                    onClick={() => addComment.mutate({ id: selected.id, admin_comment: comment }, done(t('mkt_applications.commentSaved')))}>
                    {t('mkt_applications.saveComment')}
                  </Button>
                </div>

                {['SUBMITTED', 'UNDER_REVIEW'].includes(selected.status) && (
                  <>
                    <div>
                      <Label>{t('mkt_applications.addToCampaign')}</Label>
                      <Select value={campaignId || '__none__'} onValueChange={(v) => setCampaignId(v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder={t('mkt_applications.noCampaign')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t('mkt_applications.noCampaign')}</SelectItem>
                          {matchingCampaigns.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('mkt_applications.rejectionReason')}</Label>
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('mkt_applications.rejectionRequired')} />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {selected.status === 'SUBMITTED' && (
                  <Button variant="outline" onClick={() => review.mutate({ id: selected.id }, done(t('mkt_applications.reviewStarted')))}>{t('mkt_applications.startReview')}</Button>
                )}
                {['SUBMITTED', 'UNDER_REVIEW'].includes(selected.status) && (
                  <>
                    <Button variant="outline" onClick={() => requestChanges.mutate({ id: selected.id, admin_comment: comment }, done(t('mkt_applications.changesRequested')))}>
                      {t('mkt_applications.requestChanges')}
                    </Button>
                    <Button variant="destructive" onClick={() => {
                      if (!reason.trim()) return toast.error(t('mkt_applications.reasonRequired'));
                      reject.mutate({ id: selected.id, rejection_reason: reason }, done(t('mkt_applications.rejected')));
                    }}>{t('mkt_applications.reject')}</Button>
                    <Button onClick={() => approve.mutate({ id: selected.id, campaign_id: campaignId || undefined }, done(t('mkt_applications.approved')))}>
                      {t('mkt_applications.approve')}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
